// Build human-readable insights from a scored assessment.
// All scoring math lives in the backend; this file purely derives narratives
// from the already-computed domain/extended scores (1.0 – 5.0).
//
// Used by the Results dashboard for:
//   • Plain-language interpretation
//   • Top 3 weaknesses
//   • Top 3 quick wins
//   • Recommended consultation package

import { STABILITY_LEVELS } from '@/lib/constants';

const PLAIN_INTERPRETATIONS = {
  [STABILITY_LEVELS.HIGH]:
    "Your workflows are highly stable, standardised, and predictable. You are operationally ready to layer AI and automation on top — but adoption will still depend on data readiness, staff capability, and governance. The next move is to pilot AI on your strongest workflows and measure ROI.",
  [STABILITY_LEVELS.MODERATE]:
    "Your workflows are partly stable. Some areas are ready for automation, while others still rely heavily on individuals, ad-hoc fixes, or inconsistent processes. Investing in AI right now would amplify both your strengths and your weaknesses. The right next step is to standardise the unstable workflows first, then automate.",
  [STABILITY_LEVELS.LOW]:
    "Your workflows are not yet stable enough for automation or AI. Decisions depend heavily on specific people, processes vary across team members, and errors are caught reactively. Investing in AI tools at this stage will not deliver lasting ROI. Focus first on standardising processes, documenting tribal knowledge, and digitising inputs.",
};

const QUICK_WIN_TEMPLATES = {
  'Decision Dependency':
    'Document the key decisions one person makes today. Convert 2–3 of them into a written checklist or decision rule.',
  'Process Consistency':
    'Pick your most variable workflow and define a single standard operating procedure (SOP) the whole team must follow.',
  'Repetitive Work':
    'Identify the top repeating task (per day or per week) and replace it with a template, macro, or scheduled job.',
  'Error Control':
    'Add a two-line review checklist (reviewer + signature) at the point where errors are most often caught downstream.',
  'Data Readiness':
    'Move one critical paper/PDF process into a structured spreadsheet or shared system this month.',
  'System Support':
    'Audit which tools your team actually uses vs. owns. Sunset shadow tools and consolidate on the official ones.',
  'Staff Capability':
    'Run a 60-minute internal AI literacy session so the team can describe AI in their own words and identify use-cases.',
  'AI Governance':
    'Draft a one-page AI usage policy covering data, tools allowed, and approval workflow before any pilot.',
};

// Pick the lowest-scoring domains as "weaknesses".
// Pick the matching quick-win recommendation for each.
const pickLowestDomains = (scoresObj, limit) =>
  Object.entries(scoresObj)
    .sort(([, a], [, b]) => a - b)
    .slice(0, limit)
    .map(([domain, score]) => ({ domain, score: Number(score) }));

export const getPlainLanguageInterpretation = (stabilityLevel) =>
  PLAIN_INTERPRETATIONS[stabilityLevel] || PLAIN_INTERPRETATIONS[STABILITY_LEVELS.LOW];

export const getTop3Weaknesses = (assessment) => {
  const merged = { ...assessment.domain_scores, ...assessment.extended_scores };
  return pickLowestDomains(merged, 3);
};

// --- Priority Observations (Feb 2026 redesign) ---
// Domain-level reasons + first actions for the free snapshot card.
// Wording is intentionally formal/management-oriented, not casual.
const DOMAIN_REASONS = {
  'Decision Dependency':
    'Key decisions depend on specific individuals, which limits scalability and continuity.',
  'Process Consistency':
    'Workflow execution varies between team members, reducing reliability and audit readiness.',
  'Repetitive Work':
    'Repetitive manual effort consumes capacity that could be redirected to higher-value work.',
  'Error Control':
    'Errors are caught reactively rather than prevented, exposing the organisation to rework risk.',
  'Data Readiness':
    'Data inputs are not yet in a structured form suitable for analytics or AI.',
  'System Support':
    'System coverage of the workflow is partial or fragmented across tools.',
  'Staff Capability':
    'Team familiarity with automation concepts is still developing.',
  'AI Governance':
    'Formal guardrails for AI usage are not yet in place.',
};

const DOMAIN_FIRST_ACTIONS = {
  'Decision Dependency':
    'Document the recurring decisions handled by one person and convert two of them into written rules.',
  'Process Consistency':
    'Select one workflow and define a single standard operating procedure for the whole team.',
  'Repetitive Work':
    'Identify the most repeated weekly task and pilot a template or scheduled job to replace it.',
  'Error Control':
    'Insert a short reviewer checklist at the point where errors are most often detected downstream.',
  'Data Readiness':
    'Move one critical paper or PDF process into a structured spreadsheet or shared system this month.',
  'System Support':
    'Audit the tools your team uses and consolidate on the official systems for the chosen workflow.',
  'Staff Capability':
    'Run a one-hour internal session on automation concepts and gather candidate use cases.',
  'AI Governance':
    'Draft a one-page policy covering allowed AI tools, data, and approval workflow.',
};

const enrichObservation = (entry) => ({
  ...entry,
  whyItMatters: DOMAIN_REASONS[entry.domain] || 'This domain affects overall workflow stability.',
  firstAction: DOMAIN_FIRST_ACTIONS[entry.domain] || 'Review this domain with the workflow owner before any automation.',
});

const CLUSTER_TOLERANCE = 0.5;
const ALL_STRONG_THRESHOLD = 4.0;

/**
 * Returns one of three shapes for the free Priority Observations card:
 *   { kind: 'all_strong',   items,   minScore }
 *   { kind: 'clustered',    items,   range }
 *   { kind: 'ranked',       items }
 * `items` is always the 3 lowest-scoring domains (enriched), so the UI can
 * always show the cards even if it wraps them in a neutral preamble.
 */
export const getPriorityObservations = (assessment) => {
  const merged = { ...assessment.domain_scores, ...assessment.extended_scores };
  const allValues = Object.values(merged).map(Number);
  const lowest3 = pickLowestDomains(merged, 3).map(enrichObservation);

  if (allValues.length === 0) {
    return { kind: 'ranked', items: lowest3 };
  }
  const minScore = Math.min(...allValues);
  const maxScore = Math.max(...allValues);
  if (minScore >= ALL_STRONG_THRESHOLD) {
    return { kind: 'all_strong', items: lowest3, minScore };
  }
  if (maxScore - minScore <= CLUSTER_TOLERANCE) {
    return { kind: 'clustered', items: lowest3, range: maxScore - minScore };
  }
  return { kind: 'ranked', items: lowest3 };
};

export const getTop3QuickWins = (assessment) =>
  getTop3Weaknesses(assessment).map(item => ({
    domain: item.domain,
    score: item.score,
    action: QUICK_WIN_TEMPLATES[item.domain] || 'Standardise this domain before attempting automation.',
  }));

const PACKAGE_BY_LEVEL = {
  [STABILITY_LEVELS.HIGH]: {
    name: 'AI Enablement and Scale Engagement',
    duration: 'Scope and duration discussed at consultation',
    headline: 'The findings suggest the organisation is positioned to pilot AI on its strongest workflows.',
    bullets: [
      'Workflow shortlisting and business-case validation',
      'AI / automation pilot on selected priority processes',
      'KPIs, governance, and scale-up roadmap',
    ],
  },
  [STABILITY_LEVELS.MODERATE]: {
    name: 'SMART-Workflow Transformation Programme',
    duration: 'Scope and duration discussed at consultation',
    headline: 'Standardise first, then automate. An advisory engagement is recommended to support this transition.',
    bullets: [
      'Workflow mapping workshop on selected priority areas',
      'SOP design and cross-team handover',
      'AI-readiness roadmap with selected quick-win automations',
    ],
  },
  [STABILITY_LEVELS.LOW]: {
    name: 'Workflow Stabilisation Engagement',
    duration: 'Scope and duration discussed at consultation',
    headline: 'Establishing workflow stability is recommended before investing in AI.',
    bullets: [
      'Capture of institutional knowledge and documentation',
      'Process standardisation and digital input design',
      'Change-management support for the team',
    ],
  },
};

export const getRecommendedPackage = (stabilityLevel) =>
  PACKAGE_BY_LEVEL[stabilityLevel] || PACKAGE_BY_LEVEL[STABILITY_LEVELS.LOW];
